import React, { useState } from 'react';
import type { SelectOption } from '~/components/molecule/MultiSelectInput';

import Overlay from '~/components/organism/ModalsV2/Overlay';
import Modal from '~/components/organism/ModalsV2/Modal';
import JustificationContainer from '~/components/atom/JustificationContainer';
import {
  Body,
  Heading3,
  Heading4,
  Heading5,
} from '~/components/atom/Typography';
import Input from '~/components/molecule/Input';
import MultiSelectInput from '~/components/molecule/MultiSelectInput';
import CopyCode from '~/components/atom/CopyCode';
import { APP_TYPES } from './constants';
import Button from '~/components/atom/Button';
import CollapsibleChevronBlock from '~/components/molecule/CollapsibleChevronBlock';
import Icon from '~/components/atom/Icon';
import CodeBlock from '~/components/molecule/CodeBlock';
import isValidTemplateName from '../../utils/isValidTemplateName';
import TextButton from '~/components/atom/TextButton';
import Catalog from '~/Catalog';
import StatusContainer from '~/components/organism/StatusContainer';

const text = {
  buildAFlowTemplateName: 'Bouw een flow template naam',
  flowName: 'Flow naam',
  flowDescription: 'Flow omschrijving',
  categories: 'Categorieën (gescheiden door een komma)',
  appTypes: 'Selecteer app types',
  copyFlowName: 'Kopieer flow naam',
  invalidFlowName: 'Ongeldige flow naam',
  submit: 'Voeg flow template naam toe',
  rules: 'Regels',
  availableAppTypes: 'Beschikbare app types:',
  availableAppTypesList:
    'BIEDWIJZER, VALUATION_REPORT, FUNDA, TRUSTOO, GEO_GAP, BB_WAARDECHECK, ZAPIER, REALWORKS, VBO_WAARDECHECK, HYPOTHEEKBOND, ARXGROEP, ENVEN, HOMEKEUR, WOZ_COMPLAINT, SUSTAINABILITY_REPORT, CONTAQT, STELLAX_AI',
  flowTemplateNameExample:
    'Flow name ;; Flow description ;; Category1,Category2,Category3',
  flowTemplateNameExampleWithAppTypes:
    'Flow name ;; Flow description ;; Category1,Category2,Category3 ;; AppType,AppType',
};

export type Props = {
  onClose: () => void;
  onSubmit: (flowTemplateName: string) => void;
};

const GenerateFlowTemplateNameModal: React.FCC<Props> = ({
  onClose,
  onSubmit,
}) => {
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [categories, setCategories] = useState('');
  const [appTypes, setAppTypes] = useState<Array<SelectOption>>([]);

  const trimmedFlowName = flowName.trim();
  const trimmedFlowDescription = flowDescription.trim();
  const trimmedCategories = categories
    .split(',')
    .map(category => category.trim())
    .join(',');

  const appTypesString =
    appTypes.length > 0
      ? ` ;; ${appTypes.map(type => type.payload.value).join(',')}`
      : '';

  const finalString = `${trimmedFlowName} ;; ${trimmedFlowDescription} ;; ${trimmedCategories}${appTypesString}`;

  const isValid = isValidTemplateName(finalString);

  return (
    <Overlay onClose={onClose} root="generate-flow-template-name-modal">
      <Modal>
        <JustificationContainer direction="column" gap="s" padding={['l']}>
          <Heading3 margin={[null]}>{text.buildAFlowTemplateName}</Heading3>
          <InfoBlock />
          <Input
            label={{ text: text.flowName }}
            value={flowName}
            onChange={event => setFlowName(event.target.value)}
            width="100%"
          />
          <Input
            label={{ text: text.flowDescription }}
            value={flowDescription}
            onChange={event => setFlowDescription(event.target.value)}
            width="100%"
          />

          <Input
            label={{ text: text.categories }}
            value={categories}
            onChange={event => setCategories(event.target.value)}
            width="100%"
          />
          <MultiSelectInput
            label={{ text: text.appTypes }}
            options={APP_TYPES}
            selectedOptions={appTypes}
            onSelected={option => {
              setAppTypes(prev => [...prev, option]);
            }}
            onRemoved={event =>
              setAppTypes(prev => prev.filter(type => type.key !== event.key))
            }
            width="100%"
          />

          <JustificationContainer align="center" gap="base">
            <CopyCode
              backgroundColor={{
                group: isValid ? 'success' : 'danger',
                variant: 'translucent',
              }}
              copyLabel={text.copyFlowName}
              code={finalString}
            />
          </JustificationContainer>

          {!isValid && (
            <StatusContainer level="danger" label={text.invalidFlowName} />
          )}

          <JustificationContainer
            width="100%"
            gap="base"
            align="end"
            justification="end"
          >
            <TextButton
              label={Catalog.cancel}
              onClick={onClose}
              appearance="danger"
            />
            <Button
              label={text.submit}
              onClick={() => onSubmit(finalString)}
              disabled={!isValid}
              appearance="secondary"
            />
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

const InfoBlock: React.FCC = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <CollapsibleChevronBlock
      header={
        <JustificationContainer align="center" gap="base">
          <Icon name="info" strokeWidth={2.5} />{' '}
          <Heading4 margin={[null]}>{text.rules}</Heading4>
        </JustificationContainer>
      }
      isOpen={isOpen}
      onToggle={() => setIsOpen(prev => !prev)}
      variant="info"
    >
      <Heading5>{text.flowName}</Heading5>
      <JustificationContainer align="center" gap="base">
        <CopyCode code={text.flowTemplateNameExample} />
        <Body fontWeight="semiBold">or</Body>
        <CopyCode code={text.flowTemplateNameExampleWithAppTypes} />
      </JustificationContainer>
      <Heading5 margin={[null]}>{text.availableAppTypes}</Heading5>
      <CodeBlock>{text.availableAppTypesList}</CodeBlock>
    </CollapsibleChevronBlock>
  );
};

export default GenerateFlowTemplateNameModal;
